import { useState } from 'react'

import { FxAlert, FxButton } from '@/components/fx'
import type { RecognitionArm } from '@/types/domain'

import { CockpitDialog } from './CockpitDialog'
import { applyExplanation, ARM_RUNS, documentCountSubject } from './documentsModel'
import { documentActionError } from './useDocuments'

/**
 * The bar above the table while rows are selected: re-analyse, the two KI
 * readings, delete.
 *
 * All three analysis buttons hit the same endpoint
 * (`POST /v1/templates/:id/jobs`). The difference is the `arm` — and, for an
 * arm, `apply`, which the **worker defaults to true**. A comparison run
 * started without `apply: false` rewrites the delivered values, and the next
 * run then reads its own output as the OCR baseline, agrees with itself and
 * reports full confidence on a value nobody checked. So the two KI buttons
 * open a dialog that makes the choice explicit, defaults it to the harmless
 * one, and always sends the flag.
 *
 * "Erneut analysieren" sends no `apply` at all: it is an ordinary analysis,
 * where the flag has no meaning (the worker reads it only on an arm run), and
 * sending it would suggest the two runs are the same thing.
 */
export function BulkBar({
  count,
  canWrite,
  pending,
  error,
  onAnalyse,
  onRunArm,
  onDelete,
  onClear,
}: {
  count: number
  canWrite: boolean
  pending: boolean
  error: unknown
  onAnalyse: () => void
  onRunArm: (arm: RecognitionArm, apply: boolean) => void
  onDelete: () => void
  onClear: () => void
}) {
  const [armDialog, setArmDialog] = useState<'llm_text' | 'llm_image' | null>(null)
  const [apply, setApply] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (count === 0) return null

  const run = armDialog ? ARM_RUNS[armDialog] : null

  return (
    <div className="mb-3 flex flex-col gap-2 rounded-sm bg-primary-subtle p-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-body-strong" aria-live="polite">
          {count === 1 ? '1 Dokument ausgewählt' : `${count} Dokumente ausgewählt`}
        </span>
        <span className="flex-1" />
        {canWrite && (
          <>
            <FxButton variant="tonal" className="bg-surface" disabled={pending} onClick={onAnalyse}>
              Erneut analysieren
            </FxButton>
            <FxButton
              variant="tonal"
              className="bg-surface"
              icon="auto_awesome"
              disabled={pending}
              onClick={() => {
                setApply(false)
                setArmDialog('llm_text')
              }}
            >
              {ARM_RUNS.llm_text.label}
            </FxButton>
            <FxButton
              variant="tonal"
              className="bg-surface"
              icon="auto_awesome"
              disabled={pending}
              onClick={() => {
                setApply(false)
                setArmDialog('llm_image')
              }}
            >
              {ARM_RUNS.llm_image.label}
            </FxButton>
            <FxButton variant="dangerTonal" disabled={pending} onClick={() => setDeleting(true)}>
              Löschen
            </FxButton>
          </>
        )}
        <FxButton variant="outlined" onClick={onClear}>
          Auswahl aufheben
        </FxButton>
      </div>

      {error != null && <FxAlert tone="danger">{documentActionError(error)}</FxAlert>}

      <CockpitDialog
        open={run != null}
        onOpenChange={(open) => !open && setArmDialog(null)}
        title={run ? `${run.label} starten` : ''}
        description={
          run ? (
            <>
              {documentCountSubject(count)} werden noch einmal gelesen. {run.what}
            </>
          ) : null
        }
        footer={
          <>
            <FxButton variant="outlined" onClick={() => setArmDialog(null)}>
              Abbrechen
            </FxButton>
            <FxButton
              disabled={pending}
              onClick={() => {
                if (run) onRunArm(run.arm, apply)
                setArmDialog(null)
              }}
            >
              {run?.label} starten
            </FxButton>
          </>
        }
      >
        <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
          <legend className="mb-1 text-body-strong">Was soll mit der Lesung geschehen?</legend>
          {[false, true].map((value) => (
            <label
              key={String(value)}
              className="flex cursor-pointer gap-3 rounded-sm p-3 hover:bg-primary-subtle"
            >
              <input
                type="radio"
                name="apply"
                className="mt-1 h-5 w-5 flex-none accent-[hsl(var(--fx-primary-strong))]"
                checked={apply === value}
                onChange={() => setApply(value)}
              />
              <span className="min-w-0">
                <span className="block text-body-strong">
                  {value ? 'Werte übernehmen' : 'Nur vergleichen (empfohlen)'}
                </span>
                <span className="block text-small text-text-muted">
                  {applyExplanation(value, count)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      </CockpitDialog>

      <CockpitDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="Dokumente löschen?"
        description={
          <>
            {documentCountSubject(count)} werden endgültig gelöscht, mit allen Ergebnissen und
            Korrekturen. Das lässt sich nicht rückgängig machen.
          </>
        }
        footer={
          <>
            <FxButton variant="outlined" onClick={() => setDeleting(false)}>
              Abbrechen
            </FxButton>
            <FxButton
              variant="danger"
              disabled={pending}
              onClick={() => {
                onDelete()
                setDeleting(false)
              }}
            >
              Endgültig löschen
            </FxButton>
          </>
        }
      />
    </div>
  )
}
